/**
 * v1.0 作業小遊戲的工具箱（只用 DOM＋pointer events：滑鼠、觸控都能玩）。
 *
 * 常用：
 *   const box = div('mg-item', { left: '10px', top: '20px' }, '🥬');   // 絕對定位的東西
 *   host.stage.append(box);
 *   drag(box, { stage: host.stage, onDrop: (x, y, zone) => zone?.dataset.zone === 'board' });   // 拖到某個區域
 *   zone(el, 'board')                       // 標記成可以放東西的區域（data-zone）
 *   stroke(host.stage, { onStroke: pts => … })   // 用滑鼠／手指畫線（切菜、畫圈攪拌）
 *   hold(button, dt => level += dt, () => …)    // 按住不放（裝水、運轉）
 *   gauge({ max: 2, lo: 1.3, hi: 1.5, unit: 'L' })   // 量表（有目標區間）
 *   ruler(pxPerCm, 30)                       // 比例尺
 */
export type Pt = { x: number; y: number };

export function div(cls: string, style: Partial<CSSStyleDeclaration> = {}, ...children: (Node | string | null | undefined | false)[]): HTMLDivElement {
  const d = document.createElement('div'); d.className = cls; Object.assign(d.style, style);
  for (const c of children) if (c != null && c !== false) d.append(c instanceof Node ? c : document.createTextNode(c));
  return d;
}
export function btn(label: string, onClick: () => void, cls = 'mg-btn'): HTMLButtonElement {
  const b = document.createElement('button'); b.type = 'button'; b.className = cls; b.textContent = label;
  b.addEventListener('click', e => { e.stopPropagation(); onClick(); });
  return b;
}
/** 左側說明區用：標題＋內容 */
export function sideBlock(title: string, ...children: (Node | string)[]): HTMLDivElement {
  return div('mg-side-block', {}, div('mg-side-title', {}, title), ...children);
}
/** v1.1.1 步驟一行一個（① ② ③ …），比較好讀 */
export function steps(...lines: string[]): HTMLOListElement {
  const ol = document.createElement('ol'); ol.className = 'mg-steps';
  for (const l of lines) { const li = document.createElement('li'); li.textContent = l.replace(/^[①②③④⑤⑥⑦⑧⑨]\s*/, ''); ol.append(li); }
  return ol;
}
export function place(el: HTMLElement, x: number, y: number): void { el.style.left = `${x}px`; el.style.top = `${y}px`; }
/** client 座標 → stage 內座標 */
/** v1.2.2 作業區可能被縮放（transform: scale）：畫面上的 1 px ＝ 作業區裡的 1/k px */
export function stageScale(stage: HTMLElement): number { const r = stage.getBoundingClientRect(); return stage.offsetWidth ? r.width / stage.offsetWidth || 1 : 1; }
export function local(stage: HTMLElement, cx: number, cy: number): Pt { const r = stage.getBoundingClientRect(), k = stageScale(stage); return { x: (cx - r.left) / k, y: (cy - r.top) / k }; }
/** 元素在 stage 內的位置與大小 */
export function rectIn(stage: HTMLElement, el: Element): { x: number; y: number; w: number; h: number; cx: number; cy: number } {
  const s = stage.getBoundingClientRect(), r = el.getBoundingClientRect(), k = stageScale(stage);
  return { x: (r.left - s.left) / k, y: (r.top - s.top) / k, w: r.width / k, h: r.height / k, cx: (r.left - s.left + r.width / 2) / k, cy: (r.top - s.top + r.height / 2) / k };
}
export const inRect = (p: Pt, r: { x: number; y: number; w: number; h: number }, pad = 0) => p.x >= r.x - pad && p.x <= r.x + r.w + pad && p.y >= r.y - pad && p.y <= r.y + r.h + pad;
/** 標記成可以放東西的區域 */
export function zone<T extends HTMLElement>(el: T, name: string): T { el.dataset.zone = name; return el; }

export interface DragOpts {
  stage: HTMLElement;
  /** 回傳 false＝這次不能拖（例如還沒輪到） */
  onStart?: () => boolean | void;
  onMove?: (x: number, y: number, zone: HTMLElement | null) => void;
  /** 放開：x,y 是 stage 座標；zone 是底下的 data-zone 元素。回傳 false＝彈回原位 */
  onDrop: (x: number, y: number, zone: HTMLElement | null) => boolean | void;
}
/** 讓一個（stage 裡絕對定位的）元素可以拖。回傳取消函式 */
export function drag(el: HTMLElement, o: DragOpts): () => void {
  el.style.touchAction = 'none'; el.classList.add('mg-draggable');
  let start: Pt | null = null, origin = { left: '', top: '' }, off: Pt = { x: 0, y: 0 }, over: HTMLElement | null = null;
  const zoneAt = (cx: number, cy: number) => {
    const prev = el.style.pointerEvents; el.style.pointerEvents = 'none';
    const z = (document.elementFromPoint(cx, cy) as HTMLElement | null)?.closest<HTMLElement>('[data-zone]') ?? null;
    el.style.pointerEvents = prev;
    return z && o.stage.contains(z) ? z : null;
  };
  const down = (e: PointerEvent) => {
    if (e.button !== 0 || el.classList.contains('mg-locked')) return;
    if (o.onStart && o.onStart() === false) return;
    e.preventDefault(); e.stopPropagation();
    try { el.setPointerCapture(e.pointerId); } catch { /* 舊瀏覽器 */ }
    const p = local(o.stage, e.clientX, e.clientY); const r = rectIn(o.stage, el);
    start = p; off = { x: p.x - r.x, y: p.y - r.y }; origin = { left: el.style.left, top: el.style.top };
    el.classList.add('mg-dragging');
  };
  const move = (e: PointerEvent) => {
    if (!start) return;
    const p = local(o.stage, e.clientX, e.clientY);
    place(el, p.x - off.x, p.y - off.y);
    const z = zoneAt(e.clientX, e.clientY);
    if (z !== over) { over?.classList.remove('mg-over'); over = z; over?.classList.add('mg-over'); }
    o.onMove?.(p.x, p.y, z);
  };
  const up = (e: PointerEvent) => {
    if (!start) return;
    start = null; el.classList.remove('mg-dragging'); over?.classList.remove('mg-over'); over = null;
    const p = local(o.stage, e.clientX, e.clientY);
    const ok = o.onDrop(p.x, p.y, zoneAt(e.clientX, e.clientY));
    if (ok === false && el.isConnected) { el.classList.add('mg-snap'); el.style.left = origin.left; el.style.top = origin.top; setTimeout(() => el.classList.remove('mg-snap'), 220); }
  };
  el.addEventListener('pointerdown', down); el.addEventListener('pointermove', move); el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
  return () => { el.removeEventListener('pointerdown', down); el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); };
}

export interface StrokeOpts {
  /** 畫完一筆（放開） */
  onStroke: (pts: Pt[]) => void;
  /** 畫的過程中（攪拌要即時算） */
  onPoint?: (p: Pt, pts: Pt[]) => void;
  color?: string;
  /** 只有在這個元素上按下才開始（預設：target 本身） */
  startOn?: (e: PointerEvent) => boolean;
}
/** 在 target 上用滑鼠／手指畫線（座標是 stage 座標）；線會淡出。回傳取消函式 */
export function stroke(stage: HTMLElement, target: HTMLElement, o: StrokeOpts): () => void {
  target.style.touchAction = 'none';
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg'); svg.setAttribute('class', 'mg-stroke');
  stage.append(svg);
  let pts: Pt[] | null = null; let line: SVGPolylineElement | null = null;
  const down = (e: PointerEvent) => {
    if (e.button !== 0 || (o.startOn && !o.startOn(e))) return;
    if ((e.target as HTMLElement).closest('.mg-draggable,button')) return;
    e.preventDefault();
    try { target.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    pts = [local(stage, e.clientX, e.clientY)];
    line = document.createElementNS(NS, 'polyline'); line.setAttribute('stroke', o.color ?? '#f2c84b'); svg.append(line);
  };
  const move = (e: PointerEvent) => {
    if (!pts || !line) return;
    const p = local(stage, e.clientX, e.clientY); pts.push(p);
    line.setAttribute('points', pts.map(q => `${q.x},${q.y}`).join(' '));
    o.onPoint?.(p, pts);
  };
  const up = () => {
    if (!pts || !line) return;
    const l = line; const done = pts; pts = null; line = null;
    l.classList.add('mg-fade'); setTimeout(() => l.remove(), 450);
    o.onStroke(done);
  };
  target.addEventListener('pointerdown', down); target.addEventListener('pointermove', move); target.addEventListener('pointerup', up); target.addEventListener('pointercancel', up);
  return () => { svg.remove(); target.removeEventListener('pointerdown', down); target.removeEventListener('pointermove', move); target.removeEventListener('pointerup', up); target.removeEventListener('pointercancel', up); };
}

/** 按住不放：每一幀 onTick(秒)，放開 onEnd() */
export function hold(el: HTMLElement, onTick: (dt: number) => void, onEnd?: () => void): () => void {
  el.style.touchAction = 'none';
  let raf = 0, last = 0, on = false;
  const loop = (t: number) => { if (!on) return; const dt = last ? Math.min(0.1, (t - last) / 1000) : 0; last = t; onTick(dt); raf = requestAnimationFrame(loop); };
  const down = (e: PointerEvent) => { if (e.button !== 0) return; e.preventDefault(); try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ } on = true; last = 0; el.classList.add('mg-holding'); raf = requestAnimationFrame(loop); };
  const up = () => { if (!on) return; on = false; cancelAnimationFrame(raf); el.classList.remove('mg-holding'); onEnd?.(); };
  el.addEventListener('pointerdown', down); el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up); el.addEventListener('pointerleave', up);
  return () => { on = false; cancelAnimationFrame(raf); el.removeEventListener('pointerdown', down); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); el.removeEventListener('pointerleave', up); };
}

/** 直式量表：目標區間 lo～hi 會標綠色 */
export function gauge(o: { max: number; lo: number; hi: number; unit: string; label?: string; digits?: number }): HTMLDivElement & { set(v: number): void; value(): number } {
  const fill = div('mg-gauge-fill'); const band = div('mg-gauge-band', { bottom: `${o.lo / o.max * 100}%`, height: `${(o.hi - o.lo) / o.max * 100}%` });
  const read = div('mg-gauge-read', {}, `0 ${o.unit}`);
  const g = div('mg-gauge', {}, o.label ? div('mg-gauge-label', {}, o.label) : null, div('mg-gauge-tube', {}, band, fill), read) as HTMLDivElement & { set(v: number): void; value(): number };
  let v = 0;
  g.set = (x: number) => { v = Math.max(0, Math.min(o.max, x)); fill.style.height = `${v / o.max * 100}%`; read.textContent = `${v.toFixed(o.digits ?? 1)} ${o.unit}`; g.classList.toggle('in', v >= o.lo && v <= o.hi); g.classList.toggle('over', v > o.hi); };
  g.value = () => v;
  return g;
}

/** 比例尺（cm）：每 1 cm 一格、每 5 cm 長線＋數字 */
export function ruler(pxPerCm: number, cm: number): HTMLDivElement {
  const r = div('mg-ruler', { width: `${pxPerCm * cm}px` });
  for (let i = 0; i <= cm; i++) {
    const t = div('mg-tick' + (i % 5 === 0 ? ' big' : ''), { left: `${i * pxPerCm}px` });
    if (i % 5 === 0) t.append(div('mg-tick-n', {}, String(i)));
    r.append(t);
  }
  r.append(div('mg-ruler-unit', {}, 'cm'));
  return r;
}

/** 一筆畫繞著中心轉了幾度（攪拌用；順逆時針都算） */
export function sweptAngle(pts: Pt[], c: Pt): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const a0 = Math.atan2(pts[i - 1].y - c.y, pts[i - 1].x - c.x), a1 = Math.atan2(pts[i].y - c.y, pts[i].x - c.x);
    let d = a1 - a0; if (d > Math.PI) d -= 2 * Math.PI; if (d < -Math.PI) d += 2 * Math.PI;
    total += Math.abs(d);
  }
  return total * 180 / Math.PI;
}

export const randInt = (rng: () => number, a: number, b: number) => a + Math.floor(rng() * (b - a + 1));
export const pick = <T>(rng: () => number, arr: readonly T[]): T => arr[Math.floor(rng() * arr.length)];
export function shuffled<T>(rng: () => number, arr: readonly T[]): T[] {
  const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a;
}
/** 小數點一位（顯示用） */
export const r1 = (v: number) => Math.round(v * 10) / 10;
